import { computed, ref } from "vue";
import { useHelpRequests } from "@/features/help/api/use-help";
import { useQueueStats } from "@/features/inbox/api/use-inbox";
import { useGaps } from "@/features/memory/api/use-gaps";
import { useMissions } from "@/features/missions/api/use-missions";
import type { NavCount } from "./nav-items";

/**
 * Os quatro números que o menu mostra — e nenhuma requisição nova.
 *
 * São exatamente os que a home já lê, com as MESMAS chaves de consulta: o
 * TanStack guarda uma entrada por chave, então o menu e a home compartilham o
 * cache e a resposta. Nenhum endpoint novo, nenhuma varredura de lista, e três
 * dos quatro já chegam contados pelo servidor.
 *
 * ── SEM RELÓGIO NENHUM (27/08, dono: nenhuma rota em polling) ─────────────
 *
 * A lateral vive o dia inteiro, e um relógio aqui é um relógio para sempre em
 * toda tela — foi exatamente o "tá tudo em loop". Os quatro números atualizam
 * por ACONTECIMENTO: o stream de tenant (`useQueueStream`, agora sempre ligado
 * com sessão) invalida `["inbox"]` e `["help"]` a cada mensagem e turno, as
 * mutações de cada feature invalidam as próprias chaves, e o resto acompanha o
 * foco da janela. Missões e curadoria ainda não têm stream próprio — os crachás
 * deles atualizam no foco/navegação até esse SSE existir.
 */
/** A mesma leitura da home: a fila inteira, de quem quer que seja. */
const WHOLE_QUEUE = { status: "all", assignment: "anyone", search: "" } as const;

export function useNavCounts() {
	const { data: stats } = useQueueStats(WHOLE_QUEUE, false);
	const { data: help } = useHelpRequests("open", null, true, false);
	const { data: missions } = useMissions(undefined, false);
	const { data: gaps } = useGaps(ref("pending_curation"), false);

	return computed<Record<NavCount, number>>(() => ({
		unanswered: stats.value?.unanswered ?? 0,
		help: help.value?.open ?? 0,
		// O que ainda vai mexer sozinho, contado pelo SERVIDOR sobre o log inteiro.
		// Era filtrado aqui, na lista que a tela devolve — que agora é UMA PÁGINA:
		// contar as vinte e cinco linhas à vista faria o crachá do menu mudar de
		// número conforme a página em que alguém estivesse.
		missions: missions.value?.open ?? 0,
		/**
		 * A CURADORIA — não as perguntas sem resposta.
		 *
		 * Contava `open`, que é linha por linha o mesmo conjunto do crachá de ajuda
		 * logo acima: os dois liam `human_request`, os dois mostravam 10, e nenhum
		 * dizia se eram dez coisas ou vinte. Uma pergunta esperando é UM trabalho e
		 * tem UMA casa (Pedidos de ajuda).
		 *
		 * O que só se faz em Memória é o passo seguinte: uma resposta que ainda não
		 * entrou na ficha nem na library. Enquanto não entra, os agentes continuam
		 * sem saber — que é a razão de valer um número no menu.
		 */
		// O TOTAL do servidor: a lista sem página vem cortada em 50, e o crachá não.
		gaps: gaps.value?.total ?? 0,
	}));
}
